import { FocusContext, useFocusable } from '../focus';
import { cx } from './cx';
import styles from './OnScreenKeyboard.module.css';

export type OskLayout = 'abc' | 'qwerty';

/**
 * UX-048: the keyboard was A–Z plus Espacio/Borrar/Limpiar — 29 keys, no Ñ, no accented vowel
 * and no digit. Nobody was *blocked* (search folds diacritics, so CORAZON finds "Corazón
 * Serrano" and NINA finds "Niña"), but a guest hunting for "NIÑA", "AÑOS" or "AVENTURA 2" had
 * no way to type what he was reading off a phone and nothing on the screen said the accents
 * were ignored — so he was left pressing keys that are not there.
 *
 * The grid goes from 7 columns to 8, which is exactly the 32 slots the 26 letters, the Ñ (in
 * its Spanish place, after N) and the five accented vowels need, at the same 608 px width. The
 * digits get a compact row of their own, and `hint` says the accents do not matter.
 */
const LAYOUTS: Record<OskLayout, readonly string[]> = {
  /** Figma TV-04, widened to 8: alphabetical, with Ñ where Spanish puts it. */
  abc: ['ABCDEFGH', 'IJKLMNÑO', 'PQRSTUVW', 'XYZÁÉÍÓÚ'],
  qwerty: ['QWERTYUI', 'OPASDFGH', 'JKLZXCVB', 'NMÑÁÉÍÓÚ'],
};

/** Its own 10-column row: ten keys on the letter grid would be 872 px wide. */
const DIGITS = '1234567890';

export interface OskLabels {
  space: string;
  backspace: string;
  clear: string;
  /** UX-048: one line under the keyboard saying the accents are ignored when searching. */
  hint?: string;
}

export interface OnScreenKeyboardProps {
  value: string;
  onChange: (next: string) => void;
  labels: OskLabels;
  layout?: OskLayout;
  /** Adds a 0-9 row. */
  digits?: boolean;
  maxLength?: number;
  focusKey?: string;
  className?: string;
}

interface OskKeyProps {
  id: string;
  label: string;
  span?: 1 | 2 | 3;
  action?: boolean;
  onPress: () => void;
  focusKey: string;
}

function OskKey({ id, label, span = 1, action = false, onPress, focusKey }: OskKeyProps) {
  const {
    ref,
    focused,
    focusKey: key,
  } = useFocusable<HTMLDivElement>({
    focusKey: `${focusKey}-${id}`,
    onEnterPress: onPress,
  });
  return (
    <div
      ref={ref}
      role="button"
      data-focus-key={key}
      data-focused={focused || undefined}
      data-testid={`osk-${id}`}
      className={cx(styles.key, action && styles.action, focused && styles.focused)}
      style={{ gridColumn: `span ${span}` }}
    >
      {label}
    </div>
  );
}

/**
 * Uppercase on-screen keyboard (docs/04 §3 Search): letters (+ optional digits), space,
 * backspace, clear. Emits the full value on every change; no native inputs involved.
 */
export function OnScreenKeyboard({
  value,
  onChange,
  labels,
  layout = 'abc',
  digits = false,
  maxLength = 100,
  focusKey = 'osk',
  className,
}: OnScreenKeyboardProps) {
  const { ref, focusKey: key } = useFocusable<HTMLDivElement>({
    focusKey,
    trackChildren: true,
    saveLastFocusedChild: true,
  });
  const append = (ch: string) => {
    if (value.length >= maxLength) return;
    onChange(value + ch);
  };
  /**
   * The letter block keeps its geometry — A is still the top-left key — so the digits and the
   * action row stay below it and nothing that walks the keyboard by counting presses moves.
   */
  const rows = LAYOUTS[layout];

  return (
    <FocusContext.Provider value={key}>
      <div ref={ref} className={cx(styles.keyboard, className)} data-testid="osk">
        {rows.map((row) => (
          <div key={row} className={styles.grid}>
            {row.split('').map((ch) => (
              <OskKey key={ch} id={ch} label={ch} onPress={() => append(ch)} focusKey={focusKey} />
            ))}
          </div>
        ))}
        {digits ? (
          /* No `data-testid` on the row: `[data-testid^="osk-"]` is how callers enumerate the
             keys, and a container would answer it with the whole row's text. */
          <div className={cx(styles.grid, styles.digits)}>
            {DIGITS.split('').map((ch) => (
              <OskKey key={ch} id={ch} label={ch} onPress={() => append(ch)} focusKey={focusKey} />
            ))}
          </div>
        ) : null}
        <div className={styles.grid}>
          <OskKey
            id="space"
            label={labels.space}
            span={3}
            action
            onPress={() => {
              if (value.length && !value.endsWith(' ')) append(' ');
            }}
            focusKey={focusKey}
          />
          <OskKey
            id="backspace"
            label={labels.backspace}
            span={3}
            action
            onPress={() => onChange(value.slice(0, -1))}
            focusKey={focusKey}
          />
          <OskKey
            id="clear"
            label={labels.clear}
            span={2}
            action
            onPress={() => onChange('')}
            focusKey={focusKey}
          />
        </div>
        {labels.hint ? (
          <p className={styles.hint} data-testid="osk-hint">
            {labels.hint}
          </p>
        ) : null}
      </div>
    </FocusContext.Provider>
  );
}
